import { z } from 'zod';
import { PAYMENT_METHODS } from '../constants.js';
import { cartItemSchema } from './cart.schema.js';
import { addressSchema } from './address.schema.js';
import { emailSchema, nifSchema, phoneSchema } from './auth.schema.js';

export const checkoutValidateSchema = z.object({
  items: z.array(cartItemSchema).min(1),
  postalCode: z.string().regex(/^\d{4}-\d{3}$/, 'Código postal inválido'),
  couponCode: z.string().optional(),
});

export const pickupContactSchema = z.object({
  firstName: z.string().min(1, 'Nome obrigatório').max(100),
  lastName: z.string().min(1, 'Apelido obrigatório').max(100),
});

export const checkoutInitiateSchema = z
  .object({
    items: z.array(cartItemSchema).min(1),
    guest: z
      .object({
        email: emailSchema,
        phone: phoneSchema,
      })
      .optional(),
    /**
     * Contact phone as typed at checkout, for buyers with an account too.
     * `guest.phone` only exists for guests, so without this the number a
     * logged-in buyer types was collected by the form and thrown away.
     * Not used to charge anything — MB WAY has its own `mbwayPhone`.
     */
    contactPhone: phoneSchema,
    shippingAddress: addressSchema.optional(),
    billingAddress: addressSchema.optional(),
    useShippingAsBilling: z.boolean().default(true),
    billingNif: nifSchema,
    shippingRateId: z.string().uuid().optional(),
    pickupPointId: z.string().uuid().optional(),
    pickupContact: pickupContactSchema.optional(),
    couponCode: z.string().optional(),
    paymentMethod: z.enum(PAYMENT_METHODS),
    mbwayPhone: z
      .string()
      .regex(/^(\+351)?\s?9\d{8}$/, 'Número MB Way inválido')
      .optional(),
    customerNotes: z.string().max(2000).optional(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'Deve aceitar os termos e condições' }),
    }),
  })
  .superRefine((data, ctx) => {
    const isPickup = Boolean(data.pickupPointId);
    const isDelivery = Boolean(data.shippingRateId);
    if (isPickup === isDelivery) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Escolha um método de envio (entrega ou recolha em loja)',
        path: ['shippingRateId'],
      });
      return;
    }
    if (isPickup) {
      if (!data.pickupContact) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Contacto obrigatório para recolha em loja',
          path: ['pickupContact'],
        });
      }
    } else {
      if (!data.shippingAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Morada de envio obrigatória',
          path: ['shippingAddress'],
        });
      }
    }
  });

export type CheckoutValidateInput = z.infer<typeof checkoutValidateSchema>;
export type CheckoutInitiateInput = z.infer<typeof checkoutInitiateSchema>;
export type PickupContactInput = z.infer<typeof pickupContactSchema>;
